import type {
  ApiRequestObject,
  BaseApiRequest,
  BaseApiResponse,
} from '../../../lib/flotiq-client/types/api-request';
import type { HookOptions } from './types';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { parseResponseErrors } from '../../../lib/flotiq-client/response-errors';
import { getQueryCacheKey } from '../../../lib/queryClientHelpers';

import useSelectedSpace from '../../useSelectedSpace';
import useToken from '../../useToken';

interface HookEntityObjectResult<
  T extends BaseApiResponse<any>,
  F extends BaseApiResponse<any>,
> {
  entity: T extends { ok: true } ? T['body'] : null;
  isLoading: boolean;
  status: number;
  errors: any;
  reload: () => Promise<void>;
  isFetching: boolean;
  updateEntity: (values?: ApiRequestObject) => Promise<T>;
  partialUpdateEntity: (values?: ApiRequestObject) => Promise<T>;
  deleteEntity: (params?: ApiRequestObject) => Promise<F>;
}

type HookEntityResultArrayResult<
  T extends BaseApiResponse<any>,
  F extends BaseApiResponse<any>,
> = [
  entity: T extends { ok: true } ? T['body'] : null,
  isLoading: boolean,
  errors: any,
  status: number,
  reload: () => Promise<void>,
  isFetching: boolean,
  updateEntity: (values?: ApiRequestObject) => Promise<T>,
  partialUpdateEntity: (values?: ApiRequestObject) => Promise<T>,
  deleteEntity: (params?: ApiRequestObject) => Promise<F>,
];

type HookEntityResult<
  T extends BaseApiResponse<any>,
  F extends BaseApiResponse<any>,
> = HookEntityObjectResult<T, F> & HookEntityResultArrayResult<T, F>;

/**
 * Hook builder for single entity
 */
export function createEntityHook<
  T extends BaseApiResponse<any>,
  F extends BaseApiResponse<any>,
>(
  getEntity: BaseApiRequest<T>,
  putEntity?: BaseApiRequest<T | BaseApiResponse<undefined>>,
  patchEntity?: BaseApiRequest<T | BaseApiResponse<undefined>>,
  removeEntity?: BaseApiRequest<F | BaseApiResponse<undefined>>,
  cacheKeyPrefix?: string | string[],
  idFieldName: string = 'id',
  defaultParams: ApiRequestObject = {},
  defaultOptions: HookOptions = {},
): (
  id?: string,
  hookParams?: ApiRequestObject,
  hookOptions?: HookOptions,
  cacheKey?: string,
) => HookEntityResult<T, F> {
  return (
    id?: string,
    hookParams = defaultParams,
    hookOptions = defaultOptions,
    cacheKey = '',
  ) => {
    const queryClient = useQueryClient();
    const jwt = useToken();
    const { space } = useSelectedSpace();
    const {
      checkStatus = true,
      cancelUnfinishedRequests = true,
      pause = false,
      staleTime = 0,
      gcTime = 5 * 60 * 1000, //5 minutes
    } = hookOptions || {};

    const params = useMemo(
      () => ({ [idFieldName]: id, ...hookParams }),
      [id, hookParams],
    );

    const queryKey = useMemo(
      () => getQueryCacheKey(cacheKeyPrefix, cacheKey, params, 'entity', space),
      [cacheKey, params, space],
    );

    const {
      isLoading: requestLoading,
      data: response,
      error: errors,
      refetch: reload,
      isPlaceholderData,
      isFetching,
    } = useQuery({
      queryKey,
      queryFn: async ({ signal }) => {
        const result = await getEntity(
          jwt,
          space,
          params,
          cancelUnfinishedRequests ? { signal } : {},
        );
        if (checkStatus && !result.ok) {
          throw parseResponseErrors(result.body, result.status);
        }
        return result;
      },
      enabled: !!id && !!jwt && !pause,
      staleTime,
      gcTime,
      retry: 0,
      refetchInterval: hookOptions?.refetchInterval ?? false,
      refetchIntervalInBackground:
        hookOptions?.refetchIntervalInBackground ?? false,
    });

    const isLoading = requestLoading || isPlaceholderData;

    const { entity, status } = useMemo(
      () => ({
        entity: response?.body || null,
        status: response?.status || (errors as any)?.status || 0,
      }),
      [errors, response?.body, response?.status],
    );

    const onMutateSuccess = useCallback(
      async (response: BaseApiResponse<T>, isDelete: boolean) => {
        if (response.ok) {
          queryClient.invalidateQueries({
            queryKey: [cacheKeyPrefix],
            refetchType: 'none',
          });
          queryClient.setQueryData(queryKey, ({ body }: { body: T }) => ({
            status: response.status,
            body: isDelete ? null : { ...body, ...response.body },
          }));
        }
        return response;
      },
      [queryClient, queryKey],
    );

    const onUpdateMutateSuccess = useCallback(
      async (response: BaseApiResponse<T>) => onMutateSuccess(response, false),
      [onMutateSuccess],
    );

    const onDeleteMutateSuccess = useCallback(
      async (response: BaseApiResponse<T>) => onMutateSuccess(response, true),
      [onMutateSuccess],
    );

    const { mutateAsync: updateEntity } = useMutation({
      mutationFn: async (values: ApiRequestObject = {}) =>
        putEntity?.(jwt, space, {
          [idFieldName]: entity?.[idFieldName],
          ...values,
        }) as any,
      onSuccess: onUpdateMutateSuccess,
    });

    const { mutateAsync: partialUpdateEntity } = useMutation({
      mutationFn: async (values: ApiRequestObject = {}) =>
        patchEntity?.(jwt, space, {
          [idFieldName]: entity?.[idFieldName],
          ...values,
        }) as any,
      onSuccess: onUpdateMutateSuccess,
    });

    const { mutateAsync: deleteEntity } = useMutation({
      mutationFn: async (params: ApiRequestObject = {}) =>
        removeEntity?.(jwt, space, {
          [idFieldName]: entity[idFieldName],
          ...params,
        }) as any,
      onSuccess: onDeleteMutateSuccess,
    });

    const result = [
      entity,
      isLoading,
      status,
      errors,
      reload,
      updateEntity,
      partialUpdateEntity,
      deleteEntity,
      isFetching,
    ];
    Object.assign(result, {
      entity,
      isLoading,
      status,
      errors,
      reload,
      updateEntity,
      partialUpdateEntity,
      deleteEntity,
      isFetching,
    });
    return result as HookEntityResult<T, F>;
  };
}
